import * as authSelectors from '@redux/reducers/auth';
import * as alerts from '@redux/utils/alerts';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Col, Row } from 'reactstrap';
import FormActions from 'views/components/FormActions';
import SpecialFunctionsDropdown from 'views/formComponents/SpecialFunctionsDropdown';
import { updateUnitSpecialFunctionService } from 'views/screens/Units/services';
import { getSpecialFunctionParams } from 'views/screens/Units/utils';

interface Props {
  isOpenSlidingPane: boolean;
  closeSlidingPane: () => void;
  isNew: boolean;
  unitId: number;
  unitName: string;
  specialFunctionId?: number;
  initialValues?: any;
  width?: string;
  refetchSpecialFunctions?: any;
  existingSpecialFunctionIds?: any;
}

const SlidingPaneEditUnitSpecialFunctions = (props: Props) => {
  const {
    isOpenSlidingPane,
    closeSlidingPane,
    isNew,
    unitId,
    unitName,
    specialFunctionId,
    initialValues,
    width,
    refetchSpecialFunctions,
    existingSpecialFunctionIds
  } = props;
  const { control, handleSubmit, watch, reset } = useForm();
  const intl = useIntl();
  const formValues = watch();

  const userMembership = useSelector((state) => {
    return {
      operatorId: authSelectors.getAuthUserOperatorId(state),
      organizationId: authSelectors.getAuthUserOrganizationId(state)
    };
  });

  return (
    <SlidingPane
      className="custom-sliding-pane"
      overlayClassName={'custom-overlay-sliding-pane'}
      isOpen={isOpenSlidingPane}
      title={
        isNew
          ? intl.formatMessage({ id: 'units.specialFunctionsNew' })
          : intl.formatMessage({ id: 'units.specialFunctionsEdit' })
      }
      subtitle={unitName}
      onRequestClose={closeSlidingPane}
      width={width ?? '50rem'}
    >
      <Row>
        <Col sm="12">
          <Controller
            name="specialFunctionId"
            control={control}
            defaultValue={initialValues?.specialFunctionId}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <SpecialFunctionsDropdown
                field={field}
                fieldstate={fieldState}
                unitId={unitId}
                useLabel
                forceinitialvalue={initialValues?.specialFunctionId}
                existingSpecialFunctionIds={existingSpecialFunctionIds}
                required
                isClearable
                isDisabled={isNew ? false : true}
                options={
                  !isNew
                    ? [
                        {
                          id: initialValues?.specialFunctionId,
                          value: initialValues?.specialFunctionId,
                          label: (
                            <FormattedMessage
                              id={`units.specialFunction${initialValues?.specialFunctionId}`}
                            />
                          ),
                          label_intl: intl.formatMessage({
                            id: `units.specialFunction${initialValues?.specialFunctionId}`
                          })
                        }
                      ]
                    : undefined
                }
              />
            )}
          />
        </Col>
      </Row>
      {getSpecialFunctionParams(
        formValues.specialFunctionId,
        Controller,
        control,
        initialValues,
        userMembership
      )}
      <FormActions
        variant="footer"
        saveType="button"
        onSave={handleSubmit((values) => {
          updateUnitSpecialFunctionService(
            {
              unitId: unitId,
              specialFunctionId: specialFunctionId,
              ...values
            },
            {
              successAction: (response) => {
                if (response?.success && response?.data && response?.data.length > 0) {
                  refetchSpecialFunctions();
                  closeSlidingPane();
                } else {
                  alerts.showErrorAlertFailChangesToast({
                    error: intl.formatMessage(
                      { id: 'units.specialFunctionsAssignFailMessage' },
                      { unitName: unitName }
                    )
                  });
                }
              },
              errorAction: (error) => {
                alerts.showErrorAlertFailChangesToast({
                  error: intl.formatMessage(
                    { id: 'units.specialFunctionsAssignFailMessage' },
                    { unitName: unitName }
                  )
                });
              }
            }
          );
          closeSlidingPane();
        })}
        onCancel={() => closeSlidingPane()}
      />
    </SlidingPane>
  );
};

export default SlidingPaneEditUnitSpecialFunctions;
